import React, { createContext, useCallback, useContext, useState } from 'react';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import { ASK_QUICK_CHAT_URL, eLocalStorageKeys } from 'src/constants/AppConstants';

const DEFAULT_PANEL_WIDTH = 400;
const MIN_PANEL_WIDTH = 420;
const MAX_PANEL_WIDTH = 800;

interface AskQuickContextValue {
  isOpen: boolean;
  togglePanel: () => void;
  closePanel: () => void;
  panelSize: number;
  onPanelResize: (size: number) => void;
  minPanelSize: number;
  maxPanelSize: number;
  activeAgentUrl: string | null;
  setActiveAgentUrl: (url: string | null) => void;
}

const AskQuickContext = createContext<AskQuickContextValue>({
  isOpen: false,
  togglePanel: () => {},
  closePanel: () => {},
  panelSize: DEFAULT_PANEL_WIDTH,
  onPanelResize: () => {},
  minPanelSize: MIN_PANEL_WIDTH,
  maxPanelSize: MAX_PANEL_WIDTH,
  activeAgentUrl: null,
  setActiveAgentUrl: () => {}
});

export const AskQuickProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = usePersistedState(eLocalStorageKeys.AskQuickPanelOpen, false);
  const [panelSize, setPanelSize] = usePersistedState(eLocalStorageKeys.AskQuickPanelWidth, DEFAULT_PANEL_WIDTH);
  const [activeAgentUrl, setActiveAgentUrl] = useState<string | null>(null);

  const togglePanel = useCallback(() => setIsOpen((prev: boolean) => !prev), [setIsOpen]);
  const closePanel = useCallback(() => setIsOpen(false), [setIsOpen]);
  const onPanelResize = useCallback((size: number) => setPanelSize(size), [setPanelSize]);

  return (
    <AskQuickContext.Provider
      value={{
        isOpen,
        togglePanel,
        closePanel,
        panelSize,
        onPanelResize,
        minPanelSize: MIN_PANEL_WIDTH,
        maxPanelSize: MAX_PANEL_WIDTH,
        activeAgentUrl,
        setActiveAgentUrl
      }}
    >
      {children}
    </AskQuickContext.Provider>
  );
};

export const useAskQuick = () => useContext(AskQuickContext);
